
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';

// We can reuse your caching logic from before
const holderWalletCache = new Map();

async function fetchHolderWallet(holderAddress, timeFrame) {
  try {
    const response = await axios.get(
      `https://radar-defi-api.onrender.com/history/holder/${holderAddress}/timeframe/${timeFrame}`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching holder wallet total value for ${holderAddress}:`, error);
    return [];
  }
}

async function getHolderWallet(holderAddress, timeFrame) {
  const cacheKey = `${holderAddress}_${timeFrame}`;
  const cacheEntry = holderWalletCache.get(cacheKey);
  const ONE_HOUR = 60 * 60 * 1000;
  const now = Date.now();

  if (cacheEntry && now - cacheEntry.timestamp < ONE_HOUR) {
    return cacheEntry.data;
  }

  const data = await fetchHolderWallet(holderAddress, timeFrame);
  holderWalletCache.set(cacheKey, { data, timestamp: now });
  return data;
}

/**
 * A small "sparkline" chart for a single token.
 * Expects an array of data points: [ { time: '12/1 1PM', value: 0.002 }, ...]
 */
const TokenSparkline = ({ data }) => {
  if (!data || data.length === 0) return <span style={{ color: '#999' }}>No data</span>;

  return (
    <div style={{ width: 150, height: 50 }}>
      <ResponsiveContainer>
        <LineChart data={data}>
          {/* We can hide the X/Y axes to make it look like a sparkline. */}
          <XAxis dataKey="time" hide />
          <YAxis hide domain={['auto', 'auto']} />
          <Tooltip
            contentStyle={{
              backgroundColor: '#222',
              color: '#fff',
              borderRadius: '6px',
              fontSize: '10px',
            }}
            labelStyle={{ fontSize: '10px' }}
            formatter={(val) => `$${val.toFixed(4)}`}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke="#42A5F5"
            dot={false}
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * Main component that renders a table of each token:
 *   Token Symbol | [Sparkline of historical balance] | Unclaimed Rewards | Claimed Rewards
 */
const TokensTable = ({ holderAddress, timeFrame = '24h' }) => {
  const [loading, setLoading] = useState(true);
  const [tokenHistoricalMap, setTokenHistoricalMap] = useState({}); 
  // tokenHistoricalMap will look like:
  //   {
  //       "XRP": [ {time: '2/25 12PM', value: 0.0079}, {time: '2/25 1PM', value: 0.0080}, ... ],
  //       "ETH": [ {time: '2/25 12PM', value: 0.1284}, ... ],
  //       ...
  //   }
  const [allTokens, setAllTokens] = useState([]); // e.g. ["XRP","ETH","YIN", ...]

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      const data = await getHolderWallet(holderAddress, timeFrame);
      if (!mounted) return;

      // We want to accumulate time-series for each token across all snapshots.
      // First, gather each snapshot, parse out the tokens in the "radarWalletAndRewardPackData[0]" array.

      // We'll build an object of arrays: tokenSymbol => [ {time, value}, ...]
      const tempMap = {};

      data.forEach((snap) => {
        const dt = new Date(snap.createdAt);
        // e.g. "2/25 1:00 PM"
        const timeLabel = dt.toLocaleString([], {
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          hour12: true,
        });

        const tokenArray = snap?.holderResponse?.radarWalletAndRewardPackData?.[0] || [];
        tokenArray.forEach((tk) => {
          const sym = tk._symbol || tk._name || tk._tokenAddress;
          const valUsd = parseFloat(tk._balanceOfHolderInUsd || '0');

          if (!tempMap[sym]) {
            tempMap[sym] = [];
          }
          tempMap[sym].push({
            time: timeLabel,
            value: valUsd,
          });
        });
      });

      // Then store them
      setTokenHistoricalMap(tempMap);
      // Also set allTokens as the keys
      setAllTokens(Object.keys(tempMap).sort());
      setLoading(false);
    })();

    return () => {
      mounted = false;
    };
  }, [holderAddress, timeFrame]);

  if (!holderAddress) {
    return <p>No holder address provided.</p>;
  }
  if (loading) {
    return <p>Loading token data...</p>;
  }

  // For unclaimed / claimed, you may have them per token or just total.
  // The sample snapshots show "unclaimedRewardsTotalValue" and "claimedRewardsTotalValue" 
  // at the wallet level, not broken out by token. 
  // If you truly have them per token, adjust accordingly. 
  // For demonstration, let's just show placeholders:

  return (
    <div style={{ margin: '1rem' }}>
      <h2>Token Performance Table</h2>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '14px',
          backgroundColor: 'transparent',
          color: 'white',
        }}
      >
        <thead style={{ background: 'transparent' }}>
          <tr>
            <th style={{ padding: '8px', textAlign: 'left' }}>Token</th>
            <th style={{ padding: '8px', textAlign: 'left' }}>Wallet Value (Chart)</th>
            {/* <th style={{ padding: '8px', textAlign: 'left' }}>Unclaimed Rewards</th>
            <th style={{ padding: '8px', textAlign: 'left' }}>Claimed Rewards</th> */}
          </tr>
        </thead>
        <tbody>
          {allTokens.map((sym) => {
            const chartData = tokenHistoricalMap[sym] || [];
            // For demonstration, placeholders:
            const unclaimed = 0;
            const claimed = 0;
            return (
              <tr key={sym} style={{ borderBottom: '1px solid #ddd' }}>
                <td style={{ padding: '6px' }}>{sym}</td>
                <td style={{ padding: '6px' }}>
                  <TokenSparkline data={chartData} />
                </td>
                {/* <td style={{ padding: '6px' }}>{unclaimed}</td>
                <td style={{ padding: '6px' }}>{claimed}</td> */}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TokensTable;

// import React, { useState, useEffect, useMemo } from 'react';
// import {
//   ResponsiveContainer,
//   XAxis,
//   YAxis,
//   Tooltip,
//   LineChart,
//   Line,
//   BarChart,
//   Bar,
//   ComposedChart,
// } from 'recharts';
// import { Button } from 'primereact/button';
// import { formatCurrencyExtended } from '../../utils';
// import axios from 'axios';

// /** In-memory cache for holder charts. */
// const holderWalletCache = new Map();;

// async function fetchHolderWallet(holderAddress, timeFrame) {
//     try {
//       const response = await axios.get(
//         `https://radar-defi-api.onrender.com/history/holder/${holderAddress}/timeframe/${timeFrame}`
//       );
//       return response.data;
//     } catch (error) {
//       console.error(`Error fetching holder wallet total value for ${holderAddress}:`, error);
//       return [];
//     }
//   }
  

// async function getHolderWallet(holderAddress, timeFrame) {
//     const cacheKey = `${holderAddress}_${timeFrame}`;
//     const cacheEntry = holderWalletCache.get(cacheKey);
//     const ONE_HOUR = 60 * 60 * 1000;
//     const now = Date.now();
  
//     if (cacheEntry && (now - cacheEntry.timestamp) < ONE_HOUR) {
//       return cacheEntry.data;
//     }
  
//     const data = await fetchHolderWallet(holderAddress, timeFrame);
//     holderWalletCache.set(cacheKey, { data, timestamp: now });
//     return data;
//   }

// /** 
//  * 1) "Aggregate" metrics from holderResponse (like total wallet, unclaimed, claimed, burned, sold).
//  *    We'll show them in one ComposedChart with line/bar toggles.
//  */
// const BASE_METRICS = [
//   { key: 'walletTotalValue', label: 'Wallet Value' },
//   { key: 'unclaimedRewardsTotalValue', label: 'Unclaimed Rewards' },
//   { key: 'claimedRewardsTotalValue', label: 'Claimed Rewards' },
//   { key: 'burnedAmount', label: 'Burned Amt' },
//   { key: 'totalSold', label: 'Sold' },
// ];

// const COLOR_MAP = {
//   walletTotalValue: '#42A5F5',
//   unclaimedRewardsTotalValue: '#ffd600',
//   claimedRewardsTotalValue: '#ff9100',
//   burnedAmount: '#ff1744',
//   totalSold: '#29b6f6',
// };

// /** 
//  * 2) We also want to display a second chart for each token in `radarWalletAndRewardPackData` 
//  *    (i.e., the user’s tokens). We'll gather them over time.
//  *    Each token can be toggled on or off, similar to the above approach.
//  */

// /**
//  * Our custom tooltip: can handle both "aggregate" metrics and per-token data,
//  * depending on how we pass the content in the chart.
//  */
// const CustomTooltip = ({ active, payload, label }) => {
//   if (!active || !payload || !payload.length) return null;
  
//   return (
//     <div
//       style={{
//         backgroundColor: '#222',
//         color: '#fff',
//         padding: '5px 8px',
//         borderRadius: '6px',
//         fontSize: '10px',
//       }}
//     >
//       <div style={{ marginBottom: '3px' }}>
//         <strong>{label}</strong>
//       </div>
//       {payload.map((entry) => {
//         // Decide if we want currency formatting or raw
//         let val = entry.value;
//         // For the tokens chart, we’re dealing with USD balances, so let's format it as currency
//         // For the aggregate chart, some are currency, some might be raw (like 'burnedAmount' or 'totalSold'),
//         // but we can unify it here or handle logic. 
//         // We'll assume all are effectively numeric currency-like. 
//         const safeVal = formatCurrencyExtended(val);
//         return (
//           <div key={entry.dataKey} style={{ color: entry.color }}>
//             <strong>{entry.name}:</strong>{" "}
//             <span style={{ color: 'white' }}>{safeVal}</span>
//           </div>
//         );
//       })}
//     </div>
//   );
// };

// /**
//  * HolderCharts:
//  * - Expects a `holderAddress` prop
//  * - Queries the timeseries data
//  * - Renders two chart sections:
//  *      (1) The "Aggregate Metrics" chart (wallet total, unclaimed, etc)
//  *      (2) The "Per-Token Balances" chart
//  */
// const HolderCharts = ({ holderAddress, timeFrame = '24h', onClose }) => {
//   const [aggregateSelectedMetrics, setAggregateSelectedMetrics] = useState([
//     'walletTotalValue'
//   ]); // toggles for the first chart
//   const [aggregateChartData, setAggregateChartData] = useState([]);

//   // For the tokens chart:
//   // We'll build a data array like: [ { time, 'XRP': 0.0079, 'ETH': 0.1284, 'YIN': 10.0274, ... }, ...]
//   // Then let user toggle tokens by symbol.
//   const [tokenChartData, setTokenChartData] = useState([]);
//   const [availableTokens, setAvailableTokens] = useState([]);  // e.g. ['XRP','ETH','YIN',...]
//   const [selectedTokens, setSelectedTokens] = useState([]);    // user toggles
  
//   useEffect(() => {
//     let mounted = true;
//     (async function fetchData() {
//       if (!holderAddress) return;
//       const data = await getHolderWallet(holderAddress, timeFrame);
//       if (!mounted) return;

//       // data is an array of snapshots. Each snapshot has shape:
//       // {
//       //   _id: "...",
//       //   holderAddress: "...",
//       //   holderResponse: {
//       //       radarWalletAndRewardPackData: [ [ { ...token1 }, { ...token2 }, ... ], [] ],
//       //       walletTotalValue: "2007.2498",
//       //       burnData: {
//       //         burnedAmount: {...} // or numeric? 
//       //         totalSold: ...
//       //       },
//       //       unclaimedRewardsTotalValue: "...",
//       //       claimedRewardsTotalValue: "..."
//       //   },
//       //   createdAt: "...",
//       //   ...
//       // }

//       // 1) Build "aggregateChartData"
//       // We'll store an array of { time, walletTotalValue, unclaimedRewardsTotalValue, ... }
//       const aggData = data.map((snap) => {
//         const hr = snap.holderResponse || {};
//         const burn = hr.burnData || {};

//         const timeLabel = new Date(snap.createdAt).toLocaleString([], {
//           month: 'numeric',
//           day: 'numeric',
//           hour: 'numeric',
//           hour12: true
//         });

//         // parse numeric
//         const walletTotalValue = parseFloat(hr.walletTotalValue || 0);
//         const unclaimedRewardsTotalValue = parseFloat(hr.unclaimedRewardsTotalValue || 0);
//         const claimedRewardsTotalValue = parseFloat(hr.claimedRewardsTotalValue || 0);

//         let burnedAmount = 0;
//         if (burn.burnedAmount && burn.burnedAmount.low) {
//           burnedAmount = Math.max(burn.burnedAmount.low, 0);
//         }

//         let totalSold = 0;
//         if (burn.totalSold && burn.totalSold.low) {
//           totalSold = Math.abs(burn.totalSold.low);
//         }

//         return {
//           time: timeLabel,
//           walletTotalValue,
//           unclaimedRewardsTotalValue,
//           claimedRewardsTotalValue,
//           burnedAmount,
//           totalSold,
//         };
//       });

//       // 2) Build "tokenChartData" 
//       // We want an array of points, each point has the shape:
//       // { time, 'XRP': 0.01, 'ETH': 0.05, 'DOGE': 0.002, ... }
//       //
//       // We'll do a first pass to gather all unique token symbols across all snapshots,
//       // and a second pass to fill in each snapshot's symbol->balance. 
//       // Or we can do it in a single pass.

//       // We'll keep a map by "timeLabel" => { symbol1: val, symbol2: val, ... }
//       // Then we'll transform it into an array.
//       const tokenDataByTime = {};
//       const allSymbolsSet = new Set();

//       data.forEach((snap) => {
//         const timeLabel = new Date(snap.createdAt).toLocaleString([], {
//           month: 'numeric',
//           day: 'numeric',
//           hour: 'numeric',
//           hour12: true
//         });

//         if (!tokenDataByTime[timeLabel]) {
//           tokenDataByTime[timeLabel] = {};
//         }

//         // "radarWalletAndRewardPackData" is typically an array with two subarrays
//         // The first is the main one, the second is often empty. We'll focus on [0].
//         const mainTokenArray = snap.holderResponse?.radarWalletAndRewardPackData?.[0] || [];
//         mainTokenArray.forEach((tk) => {
//           const sym = tk._symbol || tk._name || tk._tokenAddress;
//           const valUsd = parseFloat(tk._balanceOfHolderInUsd || "0");
//           tokenDataByTime[timeLabel][sym] = valUsd;
//           allSymbolsSet.add(sym);
//         });
//       });

//       // Now transform tokenDataByTime => an array sorted by chronological order
//       // (the snapshots are presumably in descending order or random, let's just rely on
//       // the order in data. We'll do a stable map.)
//       // We'll preserve the same ordering as 'aggData' (which is basically the same date ordering).
//       const finalTokenArr = aggData.map((aggRow) => {
//         const rowTime = aggRow.time;
//         return {
//           time: rowTime,
//           ...tokenDataByTime[rowTime],  // merges in each token's balance
//         };
//       });

//       // sorted set of all symbols
//       const allSymbols = Array.from(allSymbolsSet).sort();

//       setAggregateChartData(aggData);
//       setTokenChartData(finalTokenArr);
//       setAvailableTokens(allSymbols);

//       // By default, let's select up to e.g. 5 tokens. Or none. 
//       // For demonstration, let's select all. If you prefer partial, do that.
//       setSelectedTokens(allSymbols);
//     })();

//     return () => { mounted = false; };
//   }, [holderAddress, timeFrame]);

//   // Toggle logic for the aggregate metrics
//   const handleToggleMetric = (metricKey) => {
//     setAggregateSelectedMetrics(prev => {
//       if (prev.includes(metricKey)) {
//         return prev.filter(m => m !== metricKey);
//       } else {
//         return [...prev, metricKey];
//       }
//     });
//   };

//   // Toggle logic for tokens
//   const handleToggleToken = (tokenSym) => {
//     setSelectedTokens(prev => {
//       if (prev.includes(tokenSym)) {
//         return prev.filter(s => s !== tokenSym);
//       } else {
//         return [...prev, tokenSym];
//       }
//     });
//   };

//   if (!holderAddress) {
//     return <p style={{ textAlign: 'center' }}>No holder address specified.</p>;
//   }

//   /** 
//    * AGGREGATE CHART RENDER
//    * We'll do a ComposedChart. Lines for wallet/unclaimed/claimed, bars for burned/sold
//    */
//   const aggregateYAxis = BASE_METRICS.map((m) => (
//     <YAxis
//       key={`yAxis-${m.key}`}
//       yAxisId={m.key}
//       domain={['auto', 'auto']}
//       hide={true}
//     />
//   ));

//   const aggregateLines = BASE_METRICS
//     .filter(m => aggregateSelectedMetrics.includes(m.key))
//     .filter(m => 
//       ['walletTotalValue','unclaimedRewardsTotalValue','claimedRewardsTotalValue'].includes(m.key)
//     )
//     .map((m) => (
//       <Line
//         key={`line-${m.key}`}
//         type="monotone"
//         dataKey={m.key}
//         yAxisId={m.key}
//         stroke={COLOR_MAP[m.key] || '#bbb'}
//         dot={false}
//         strokeWidth={1.5}
//       />
//     ));

//   const aggregateBars = BASE_METRICS
//     .filter(m => aggregateSelectedMetrics.includes(m.key))
//     .filter(m => ['burnedAmount','totalSold'].includes(m.key))
//     .map((m) => (
//       <Bar
//         key={`bar-${m.key}`}
//         dataKey={m.key}
//         yAxisId={m.key}
//         fill={COLOR_MAP[m.key] || '#999'}
//       />
//     ));

//   // TOKEN CHART RENDER
//   // We’ll do a single LineChart where each token is a line. 
//   // The data is in `tokenChartData`. Keys are token symbol => numeric. 
//   // We can build a color palette for each token or randomize. 
//   // Simple approach: hash the token symbol and pick from list.
//   const colorPalette = [
//     '#42A5F5','#ab47bc','#f44336','#ffa726','#26a69a','#00acc1','#d4e157',
//     '#5e35b1','#c6ff00','#ff5252','#ffd600','#9ccc65','#9e9d24','#66bb6a',
//     '#ffa733','#8d6e63','#607d8b','#ec407a','#1de9b6','#304ffe','#7c4dff',
//   ];

//   function getColorForSymbol(sym) {
//     // just do a quick hash
//     let hash = 0;
//     for (let i=0; i<sym.length; i++) {
//       hash = sym.charCodeAt(i) + ((hash << 5) - hash);
//       hash = hash & hash; // convert to 32bit integer
//     }
//     const idx = Math.abs(hash) % colorPalette.length;
//     return colorPalette[idx];
//   }

//   // We dynamically create a separate <Line> for each token that is in selectedTokens
//   const tokenLines = availableTokens
//     .filter(sym => selectedTokens.includes(sym))
//     .map(sym => (
//       <Line
//         key={`token-line-${sym}`}
//         type="monotone"
//         dataKey={sym}
//         stroke={getColorForSymbol(sym)}
//         dot={false}
//         strokeWidth={1.5}
//       />
//     ));

//   return (
//     <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
//       <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
//         <Button 
//           label="Close" 
//           onClick={onClose} 
//           style={{ marginBottom: '4px' }} 
//         />
//       </div>

//       {/* 1) AGGREGATE CHART */}
//       <div style={{ background: '#f9f9f9', padding: '6px' }}>
//         <h3 style={{ margin: '4px 0', fontSize: '14px' }}>Aggregate Metrics</h3>
//         {aggregateChartData.length > 0 ? (
//           <>
//             <div style={{ width: '100%', height: '300px' }}>
//               <ResponsiveContainer>
//                 <ComposedChart data={aggregateChartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
//                   <XAxis
//                     dataKey="time"
//                     axisLine={false}
//                     tickLine={false}
//                     tick={{ fontSize: 10 }}
//                     tickMargin={5}
//                   />
//                   {aggregateYAxis}
//                   <Tooltip content={<CustomTooltip />} />
//                   {aggregateLines}
//                   {aggregateBars}
//                 </ComposedChart>
//               </ResponsiveContainer>
//             </div>
            
//             {/* Toggle Buttons for Aggregates */}
//             <div style={{
//               display: 'grid',
//               gridTemplateColumns: 'repeat(5, 1fr)',
//               gap: '4px',
//               marginTop: '4px',
//               marginBottom: '4px',
//             }}>
//               {BASE_METRICS.map((m) => {
//                 const isSelected = aggregateSelectedMetrics.includes(m.key);
//                 return (
//                   <Button
//                     key={m.key}
//                     label={m.label}
//                     onClick={() => handleToggleMetric(m.key)}
//                     style={{
//                       padding: '4px 0',
//                       fontSize: '11px',
//                       fontWeight: 'bold',
//                       textAlign: 'center',
//                       backgroundColor: isSelected ? (COLOR_MAP[m.key] || '#888') : '#ddd',
//                       color: isSelected ? '#fff' : '#444',
//                       borderRadius: '4px'
//                     }}
//                   />
//                 );
//               })}
//             </div>
//           </>
//         ) : (
//           <p style={{ textAlign: 'center', fontSize: '12px', color: '#777' }}>
//             Loading aggregate data...
//           </p>
//         )}
//       </div>

//       {/* 2) TOKEN CHART */}
//       <div style={{ background: '#f9f9f9', padding: '6px' }}>
//         <h3 style={{ margin: '4px 0', fontSize: '14px' }}>Per-Token Balances (USD)</h3>
//         {tokenChartData.length > 0 ? (
//           <>
//             <div style={{ width: '100%', height: '300px' }}>
//               <ResponsiveContainer>
//                 <LineChart
//                   data={tokenChartData}
//                   margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
//                 >
//                   <XAxis
//                     dataKey="time"
//                     axisLine={false}
//                     tickLine={false}
//                     tick={{ fontSize: 10 }}
//                     tickMargin={5}
//                   />
//                   <YAxis
//                     domain={['auto', 'auto']}
//                     tick={{ fontSize: 10 }}
//                   />
//                   <Tooltip content={<CustomTooltip />} />
//                   {tokenLines}
//                 </LineChart>
//               </ResponsiveContainer>
//             </div>

//             {/* Token toggles */}
//             <div style={{
//               display: 'grid',
//               gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
//               gap: '4px',
//               marginTop: '4px',
//             }}>
//               {availableTokens.map((sym) => {
//                 const isSelected = selectedTokens.includes(sym);
//                 return (
//                   <Button
//                     key={sym}
//                     label={sym}
//                     onClick={() => handleToggleToken(sym)}
//                     style={{
//                       padding: '4px 0',
//                       fontSize: '11px',
//                       fontWeight: 'bold',
//                       textAlign: 'center',
//                       backgroundColor: isSelected ? getColorForSymbol(sym) : '#ddd',
//                       color: isSelected ? '#fff' : '#444',
//                       borderRadius: '4px'
//                     }}
//                   />
//                 );
//               })}
//             </div>
//           </>
//         ) : (
//           <p style={{ textAlign: 'center', fontSize: '12px', color: '#777' }}>
//             Loading token data...
//           </p>
//         )}
//       </div>
//     </div>
//   );
// };

// export default HolderCharts;

